<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

import { toast } from "@/shared/lib"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  FormAlert,
  Label,
  Textarea,
} from "@/shared/ui"

import type { MappedBookingError } from "../model/mapBookingError"
import { useRecordsStore } from "../model/store"

const store = useRecordsStore()
const { t } = useI18n()

const open = computed(() => store.activeSheet === "cancel")

// --- Стан форми ---
const reason = ref("")
const announceText = ref("")

// --- Помилки ---
const INLINE_KEYS = new Set<string>([
  "journal.errors.concurrentModification",
  "journal.errors.notEditable",
])

const generalError = computed(() => {
  const key = store.mutationErrorKey
  if (!key || !INLINE_KEYS.has(key)) return undefined
  return t(key)
})

const showReload = computed(
  () => store.mutationErrorKey === "journal.errors.concurrentModification",
)

const isTerminal = computed(
  () => store.mutationErrorKey === "journal.errors.notEditable",
)

// --- Ефекти ---

watch(open, (isOpen) => {
  if (!isOpen) return
  reason.value = ""
  announceText.value = ""
}, { immediate: true })

// --- Дії ---

function handleCancel(): void {
  if (store.mutationStatus === 'pending') return
  const shouldRefresh = isTerminal.value
  store.closeSheet()
  if (shouldRefresh) {
    void store.refreshDay()
  }
}

async function handleConfirm(): Promise<void> {
  const booking = store.editingBooking
  if (!booking) return

  try {
    await store.submitCancel(
      booking.id,
      booking.version,
      reason.value.trim() || undefined,
    )
    announceText.value = t("journal.a11y.cancelledAnnounce")
  } catch (err) {
    const mapped = err as MappedBookingError
    if (mapped.surface === "toast") {
      if (mapped.toastVariant === "info") {
        toast.info(t(mapped.i18nKey))
      } else {
        toast.error(t(mapped.i18nKey))
      }
      if (mapped.i18nKey === "journal.errors.notFound") {
        store.closeSheet()
        void store.refreshDay()
      }
    }
  }
}

async function handleReload(): Promise<void> {
  await store.reloadForConcurrency()
}
</script>

<template>
  <!-- Live region: поза порталом, завжди в DOM -->
  <span aria-live="polite" class="sr-only">{{ announceText }}</span>

  <AlertDialog :open="open" @update:open="(v) => { if (!v) handleCancel() }">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t("journal.cancel.title") }}</AlertDialogTitle>
        <AlertDialogDescription>{{ t("journal.cancel.description") }}</AlertDialogDescription>
      </AlertDialogHeader>

      <!-- Inline помилка -->
      <FormAlert
        v-if="generalError"
        data-testid="form-error"
        variant="destructive"
        :message="generalError"
      />

      <!-- Кнопка «Оновити» для concurrent_modification -->
      <Button
        v-if="showReload"
        type="button"
        variant="outline"
        size="sm"
        data-testid="btn-reload"
        :disabled="store.mutationStatus === 'pending'"
        @click="handleReload"
      >
        {{ t("journal.errors.reloadAction") }}
      </Button>

      <!-- Причина (необов'язково) -->
      <div class="flex flex-col gap-1.5">
        <Label for="input-cancel-reason">{{ t("journal.cancel.reason") }}</Label>
        <Textarea
          id="input-cancel-reason"
          v-model="reason"
          data-testid="input-reason"
          :disabled="store.mutationStatus === 'pending'"
        />
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel @click="handleCancel">
          {{ t("journal.cancel.close") }}
        </AlertDialogCancel>
        <Button
          type="button"
          variant="destructive"
          data-testid="btn-confirm"
          :loading="store.mutationStatus === 'pending'"
          :disabled="isTerminal"
          @click="handleConfirm"
        >
          {{ t("journal.cancel.confirm") }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
